import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { CategorySidebar } from '@/components/layout/CategorySidebar';
import { MaterialCard } from '@/components/materials/MaterialCard';
import { MaterialList } from '@/components/materials/MaterialList';
import { ColumnPickerButton, OPTIONAL_COLUMNS } from '@/components/materials/ColumnPickerButton';
import { AdvancedFilterSidebar } from '@/components/search/AdvancedFilterSidebar';
import { ActiveFilterTags } from '@/components/search/ActiveFilterTags';
import { FilterPresetManager } from '@/components/search/FilterPresetManager';
import { getMaterials, getSitesPublic, getCountries, getWatchlist, addToWatchlist, removeFromWatchlist } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Filter, X, SlidersHorizontal, Loader2, Ruler, PlusCircle, Grid, List } from 'lucide-react';
import { PostWantedAdDialog } from '@/components/wanted/PostWantedAdDialog';

// All possible filter keys for URL persistence
const ALL_FILTER_KEYS = [
  'search', 'category_id', 'site_id', 'country', 'age_bucket', 'age_buckets',
  'manufacturer', 'min_width', 'max_width', 'min_thickness', 'max_thickness',
  'core_diameters', 'certifications', 'customer_specific', 'compliance_flag',
  'min_quantity', 'quantity_unit', 'min_price', 'max_price', 'sort_by', 'sort_order'
];

// URL param mappings (shorter keys for cleaner URLs)
const URL_PARAM_MAP = {
  search: 'q',
  category_id: 'category',
  site_id: 'site',
  country: 'country',
  age_bucket: 'age',
  age_buckets: 'ages',
  manufacturer: 'manufacturer',
  min_width: 'w_min',
  max_width: 'w_max',
  min_thickness: 't_min',
  max_thickness: 't_max',
  core_diameters: 'cores',
  certifications: 'certs',
  customer_specific: 'cust_spec',
  compliance_flag: 'compliant',
  min_quantity: 'qty_min',
  quantity_unit: 'qty_unit',
  min_price: 'p_min',
  max_price: 'p_max',
  sort_by: 'sort',
  sort_order: 'order'
};

// Reverse mapping
const REVERSE_URL_MAP = Object.fromEntries(
  Object.entries(URL_PARAM_MAP).map(([k, v]) => [v, k])
);

// Initialize filters from URL
const initFiltersFromURL = (searchParams) => {
  const filters = {
    search: '',
    category_id: '',
    site_id: '',
    country: '',
    age_bucket: '',
    age_buckets: '',
    manufacturer: '',
    min_width: '',
    max_width: '',
    min_thickness: '',
    max_thickness: '',
    core_diameters: '',
    certifications: '',
    customer_specific: '',
    compliance_flag: '',
    min_quantity: '',
    quantity_unit: 'sqm',
    min_price: '',
    max_price: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  };

  for (const [urlKey, value] of searchParams.entries()) {
    const filterKey = REVERSE_URL_MAP[urlKey] || urlKey;
    if (ALL_FILTER_KEYS.includes(filterKey)) {
      // Handle compliance_flag specially (boolean)
      if (filterKey === 'compliance_flag') {
        filters[filterKey] = value === 'true' ? true : (value === 'false' ? false : '');
      } else {
        filters[filterKey] = value;
      }
    }
  }

  return filters;
};

const WIDTH_MIN = 0;
const WIDTH_MAX = 2000;
const WIDTH_STEP = 10;

const WidthSlicerContent = ({ minWidth, maxWidth, onApply, onClear }) => {
  const [range, setRange] = useState([
    minWidth ? Number(minWidth) : WIDTH_MIN,
    maxWidth ? Number(maxWidth) : WIDTH_MAX
  ]);

  useEffect(() => {
    setRange([
      minWidth ? Number(minWidth) : WIDTH_MIN,
      maxWidth ? Number(maxWidth) : WIDTH_MAX
    ]);
  }, [minWidth, maxWidth]);

  const handleSliderChange = (value) => {
    setRange(value);
  };

  const handleSliderCommit = (value) => {
    const min = value[0] === WIDTH_MIN ? '' : String(value[0]);
    const max = value[1] === WIDTH_MAX ? '' : String(value[1]);
    onApply(min, max);
  };

  const handleInputChange = (index, raw) => {
    const val = raw === '' ? (index === 0 ? WIDTH_MIN : WIDTH_MAX) : Math.max(WIDTH_MIN, Math.min(WIDTH_MAX, Number(raw)));
    const next = [...range];
    next[index] = val;
    if (next[0] > next[1]) {
      next[index === 0 ? 1 : 0] = val;
    }
    setRange(next);
    const min = next[0] === WIDTH_MIN ? '' : String(next[0]);
    const max = next[1] === WIDTH_MAX ? '' : String(next[1]);
    onApply(min, max);
  };

  const isActive = range[0] !== WIDTH_MIN || range[1] !== WIDTH_MAX;

  return (
    <div className="space-y-4" data-testid="width-slicer-content">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Breite (mm)</span>
        {isActive && (
          <button
            onClick={() => { setRange([WIDTH_MIN, WIDTH_MAX]); onClear(); }}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="width-slicer-clear"
          >
            Zurücksetzen
          </button>
        )}
      </div>
      <Slider
        value={range}
        min={WIDTH_MIN}
        max={WIDTH_MAX}
        step={WIDTH_STEP}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommit}
        className="my-4"
        data-testid="width-range-slider"
      />
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={WIDTH_MIN}
          max={WIDTH_MAX}
          value={range[0]}
          onChange={(e) => handleInputChange(0, e.target.value)}
          className="h-8 text-sm text-center"
          data-testid="width-slicer-min-input"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          type="number"
          min={WIDTH_MIN}
          max={WIDTH_MAX}
          value={range[1]}
          onChange={(e) => handleInputChange(1, e.target.value)}
          className="h-8 text-sm text-center"
          data-testid="width-slicer-max-input"
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
        <span>0 mm</span>
        <span>500</span>
        <span>1000</span>
        <span>1500</span>
        <span>2000 mm</span>
      </div>
    </div>
  );
};

export const SearchPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [sites, setSites] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  
  // Initialize filters from URL
  const [filters, setFilters] = useState(() => initFiltersFromURL(searchParams));
  
  // Debounce timer ref
  const debounceRef = useRef(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [wantedAdOpen, setWantedAdOpen] = useState(false);

  // View mode + column picker (shared localStorage keys with HomePage)
  const [viewMode, setViewModeState] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    const stored = window.localStorage.getItem('materials.viewMode');
    return stored === 'list' || stored === 'grid' ? stored : 'grid';
  });
  const setViewMode = (mode) => {
    setViewModeState(mode);
    try { window.localStorage.setItem('materials.viewMode', mode); } catch (e) { /* ignore */ }
  };
  const [optionalCols, setOptionalCols] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem('materials.listColumns');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((c) => OPTIONAL_COLUMNS.includes(c));
    } catch (e) {
      return [];
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem('materials.listColumns', JSON.stringify(optionalCols)); } catch (e) { /* ignore */ }
  }, [optionalCols]);
  const toggleCol = (col) => {
    setOptionalCols((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };
  const handleSort = (field) => {
    if (filters.sort_by === field) {
      handleFilterChange({ sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc' });
    } else {
      handleFilterChange({ sort_by: field, sort_order: 'asc' });
    }
  };

  useEffect(() => {
    loadSites();
    loadCountries();
    if (user) loadWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Debounced material loading
  useEffect(() => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setIsFiltering(true);

    // Set new debounced call
    debounceRef.current = setTimeout(() => {
      loadMaterials();
      updateURLParams();
      setIsFiltering(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const loadSites = async () => {
    try {
      const response = await getSitesPublic();
      setSites(response.data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await getCountries();
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadWatchlist = async () => {
    try {
      const response = await getWatchlist();
      setWatchlist(response.data.map(item => item.material_id));
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    }
  };

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      };
      
      // Basic filters
      if (filters.search) params.search = filters.search;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.country) params.country = filters.country;
      if (filters.manufacturer) params.manufacturer = filters.manufacturer;
      
      // Technical specifications
      if (filters.min_width) params.min_width = parseFloat(filters.min_width);
      if (filters.max_width) params.max_width = parseFloat(filters.max_width);
      if (filters.min_thickness) params.min_thickness = parseFloat(filters.min_thickness);
      if (filters.max_thickness) params.max_thickness = parseFloat(filters.max_thickness);
      if (filters.core_diameters) params.core_diameters = filters.core_diameters;
      
      // Material properties
      if (filters.certifications) params.certifications = filters.certifications;
      if (filters.customer_specific) params.customer_specific = filters.customer_specific;
      if (filters.compliance_flag === true) params.compliance_flag = true;
      
      // Stock details
      if (filters.min_quantity) params.min_quantity = parseFloat(filters.min_quantity);
      if (filters.quantity_unit) params.quantity_unit = filters.quantity_unit;
      if (filters.age_buckets) params.age_buckets = filters.age_buckets;
      else if (filters.age_bucket) params.age_bucket = filters.age_bucket;
      
      // Price range
      if (filters.min_price) params.min_price = parseFloat(filters.min_price);
      if (filters.max_price) params.max_price = parseFloat(filters.max_price);
      
      const response = await getMaterials(params);
      setMaterials(response.data);
      setTotalCount(response.data.length);
    } catch (error) {
      console.error('Failed to load materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    
    for (const [filterKey, urlKey] of Object.entries(URL_PARAM_MAP)) {
      const value = filters[filterKey];
      // Skip empty values and defaults
      if (value === '' || value === undefined || value === null) continue;
      if (filterKey === 'sort_by' && value === 'created_at') continue;
      if (filterKey === 'sort_order' && value === 'desc') continue;
      if (filterKey === 'quantity_unit' && value === 'sqm') continue;
      
      // Handle boolean
      if (typeof value === 'boolean') {
        params.set(urlKey, value.toString());
      } else {
        params.set(urlKey, value);
      }
    }
    
    setSearchParams(params);
  };

  const handleWatchlistToggle = async (materialId) => {
    if (!user) {
      toast.error('Please login to use watchlist');
      return;
    }

    try {
      if (watchlist.includes(materialId)) {
        await removeFromWatchlist(materialId);
        setWatchlist(prev => prev.filter(id => id !== materialId));
        toast.success(t('material.removeFromWatchlist'));
      } else {
        await addToWatchlist(materialId);
        setWatchlist(prev => [...prev, materialId]);
        toast.success(t('material.addToWatchlist'));
      }
    } catch (error) {
      toast.error('Failed to update watchlist');
    }
  };

  // Handle filter changes (merges with existing filters)
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  // Handle removing a single filter
  const handleRemoveFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      category_id: '',
      site_id: '',
      country: '',
      age_bucket: '',
      age_buckets: '',
      manufacturer: '',
      min_width: '',
      max_width: '',
      min_thickness: '',
      max_thickness: '',
      core_diameters: '',
      certifications: '',
      customer_specific: '',
      compliance_flag: '',
      min_quantity: '',
      quantity_unit: 'sqm',
      min_price: '',
      max_price: '',
      sort_by: 'created_at',
      sort_order: 'desc'
    });
    setPage(1);
  }, []);

  // Clear only advanced filters
  const clearAdvancedFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      min_width: '',
      max_width: '',
      min_thickness: '',
      max_thickness: '',
      core_diameters: '',
      certifications: '',
      customer_specific: '',
      compliance_flag: '',
      min_quantity: '',
      quantity_unit: 'sqm',
      age_buckets: ''
    }));
    setPage(1);
  }, []);

  // Apply preset filters
  const applyPreset = useCallback((presetFilters) => {
    // Start with default filter state
    const newFilters = {
      search: filters.search, // Keep current search
      category_id: '',
      site_id: '',
      country: '',
      age_bucket: '',
      age_buckets: '',
      manufacturer: '',
      min_width: '',
      max_width: '',
      min_thickness: '',
      max_thickness: '',
      core_diameters: '',
      certifications: '',
      customer_specific: '',
      compliance_flag: '',
      min_quantity: '',
      quantity_unit: 'sqm',
      min_price: '',
      max_price: '',
      sort_by: filters.sort_by, // Keep current sort
      sort_order: filters.sort_order
    };
    
    // Apply preset filters
    Object.keys(presetFilters).forEach(key => {
      if (presetFilters[key] !== undefined && presetFilters[key] !== null) {
        newFilters[key] = presetFilters[key];
      }
    });
    
    setFilters(newFilters);
    setPage(1);
  }, [filters.search, filters.sort_by, filters.sort_order]);

  // Count active filters (excluding search, sort, and defaults)
  const activeFilterCount = [
    filters.category_id,
    filters.site_id,
    filters.country,
    filters.age_bucket,
    filters.age_buckets,
    filters.manufacturer,
    filters.min_width,
    filters.max_width,
    filters.min_thickness,
    filters.max_thickness,
    filters.core_diameters,
    filters.certifications,
    filters.customer_specific,
    filters.compliance_flag === true ? 'true' : '',
    filters.min_quantity,
    filters.min_price,
    filters.max_price
  ].filter(Boolean).length;

  // Basic filter panel (site, country, manufacturer, price)
  const BasicFilterPanel = () => (
    <div className="space-y-4">
      {/* Site */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{t('filter.site')}</Label>
        <Select
          value={filters.site_id || 'all'}
          onValueChange={(value) => handleFilterChange({ site_id: value === 'all' ? '' : value })}
        >
          <SelectTrigger className="h-8 text-sm" data-testid="filter-site-select">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map((site) => (
              <SelectItem key={site.site_id} value={site.site_id}>
                {site.site_name} ({site.city})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{t('filter.country')}</Label>
        <Select
          value={filters.country || 'all'}
          onValueChange={(value) => handleFilterChange({ country: value === 'all' ? '' : value })}
        >
          <SelectTrigger className="h-8 text-sm" data-testid="filter-country-select">
            <SelectValue placeholder={t('filter.allCountries')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filter.allCountries')}</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Manufacturer */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{t('filter.manufacturer')}</Label>
        <Input
          placeholder="e.g. 3M, BASF..."
          value={filters.manufacturer}
          onChange={(e) => handleFilterChange({ manufacturer: e.target.value })}
          className="h-8 text-sm"
        />
      </div>

      {/* Price Range */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">{t('filter.price')} (EUR)</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder={t('filter.min', 'Min')}
            value={filters.min_price}
            onChange={(e) => handleFilterChange({ min_price: e.target.value })}
            className="h-8 text-sm"
          />
          <Input
            type="number"
            placeholder={t('filter.max', 'Max')}
            value={filters.max_price}
            onChange={(e) => handleFilterChange({ max_price: e.target.value })}
            className="h-8 text-sm"
          />
        </div>
      </div>

      {activeFilterCount > 0 && (
        <Button variant="outline" onClick={clearFilters} className="w-full h-8 text-sm">
          <X className="h-3 w-3 mr-2" />
          {t('filter.clearFilters', 'Clear Filters')} ({activeFilterCount})
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="search-page">
      <Header onSearch={(q) => handleFilterChange({ search: q })} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-20 space-y-4 max-h-[calc(100vh-6rem)] overflow-y-auto pb-4">
              {/* Category Sidebar */}
              <CategorySidebar
                selectedCategory={filters.category_id}
                onSelectCategory={(id) => handleFilterChange({ category_id: id || '' })}
              />
              
              {/* Filter Presets */}
              <FilterPresetManager
                filters={filters}
                onApplyPreset={applyPreset}
                className="px-1"
              />
              
              {/* Basic Filters */}
              <div className="p-4 border rounded-lg bg-card">
                <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('filter.basicFilters', 'Filters')}
                </h3>
                <BasicFilterPanel />
              </div>

              <Separator />
              
              {/* Advanced Technical Filters */}
              <div className="p-4 border rounded-lg bg-card">
                <AdvancedFilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearAdvancedFilters}
                />
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Search Header */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h1 className="font-manrope text-2xl font-bold">
                    {filters.search ? `${t('search.resultsFor', 'Results for')} "${filters.search}"` : t('search.allMaterials', 'All Materials')}
                  </h1>
                  <p className="text-muted-foreground text-sm flex items-center gap-2">
                    {isFiltering ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t('search.filtering', 'Filtering...')}
                      </>
                    ) : loading ? (
                      '...'
                    ) : (
                      `${materials.length} ${t('search.materialsFound', 'materials found')}`
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 gap-1.5 hidden sm:inline-flex"
                    onClick={() => user ? setWantedAdOpen(true) : navigate('/login?next=/wanted')}
                    data-testid="search-post-wanted-ad-btn"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Gesuch einstellen
                  </Button>
                  {/* Mobile Filter Button */}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                        <Filter className="h-4 w-4 mr-2" />
                        {t('filter.filters', 'Filters')}
                        {activeFilterCount > 0 && (
                          <Badge className="ml-2 bg-orange-500">{activeFilterCount}</Badge>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle>{t('filter.filters', 'Filters')}</SheetTitle>
                        <SheetDescription className="sr-only">
                          {t('filter.filterDescription', 'Filter materials by various criteria')}
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6 space-y-6">
                        <FilterPresetManager
                          filters={filters}
                          onApplyPreset={applyPreset}
                        />
                        <Separator />
                        <BasicFilterPanel />
                        <Separator />
                        <AdvancedFilterSidebar
                          filters={filters}
                          onFilterChange={handleFilterChange}
                          onClearFilters={clearAdvancedFilters}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>

                  {/* Width Slicer */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "gap-2 min-w-[140px] justify-start",
                          (filters.min_width || filters.max_width) && "border-orange-400 bg-orange-50 dark:bg-orange-950/20"
                        )}
                        data-testid="width-slicer-trigger"
                      >
                        <Ruler className="h-4 w-4 shrink-0" />
                        <span className="truncate text-sm">
                          {filters.min_width || filters.max_width
                            ? `${filters.min_width || '0'} – ${filters.max_width || '2000'} mm`
                            : t('filter.width', 'Breite (mm)')
                          }
                        </span>
                        {(filters.min_width || filters.max_width) && (
                          <span
                            role="button"
                            className="ml-auto shrink-0 inline-flex items-center justify-center"
                            data-testid="width-slicer-clear-btn"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleFilterChange({ min_width: '', max_width: '' });
                            }}
                          >
                            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4" align="end" data-testid="width-slicer-popover">
                      <WidthSlicerContent
                        minWidth={filters.min_width}
                        maxWidth={filters.max_width}
                        onApply={(min, max) => handleFilterChange({ min_width: min, max_width: max })}
                        onClear={() => handleFilterChange({ min_width: '', max_width: '' })}
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Sort */}
                  <Select
                    value={`${filters.sort_by}-${filters.sort_order}`}
                    onValueChange={(value) => {
                      const [sort_by, sort_order] = value.split('-');
                      handleFilterChange({ sort_by, sort_order });
                    }}
                  >
                    <SelectTrigger className="w-48" data-testid="sort-select">
                      <SelectValue placeholder={t('sort.sortBy', 'Sort by')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at-desc">{t('sort.newestFirst', 'Newest First')}</SelectItem>
                      <SelectItem value="created_at-asc">{t('sort.oldestFirst', 'Oldest First')}</SelectItem>
                      <SelectItem value="list_price_kEUR-asc">{t('sort.priceLowHigh', 'Price: Low to High')}</SelectItem>
                      <SelectItem value="list_price_kEUR-desc">{t('sort.priceHighLow', 'Price: High to Low')}</SelectItem>
                      <SelectItem value="quantity-desc">{t('sort.quantityHighLow', 'Quantity: High to Low')}</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* View toggle Grid/List */}
                  <div className="flex border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="rounded-none h-9"
                      data-testid="view-grid-btn"
                      aria-label={t('view.grid', 'Grid')}
                    >
                      <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="rounded-none h-9"
                      data-testid="view-list-btn"
                      aria-label={t('view.list', 'List')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>

                  {viewMode === 'list' && (
                    <ColumnPickerButton
                      optionalCols={optionalCols}
                      onToggle={toggleCol}
                      onClear={() => setOptionalCols([])}
                      className="h-9 gap-2"
                    />
                  )}
                </div>
              </div>

              {/* Active Filter Tags */}
              <ActiveFilterTags
                filters={filters}
                sites={sites}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={clearFilters}
                className="mt-4"
              />
            </div>

            {/* Results Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-lg" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : materials.length > 0 ? (
              viewMode === 'list' ? (
                <MaterialList
                  materials={materials}
                  optionalCols={optionalCols}
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onSort={handleSort}
                  watchlist={watchlist}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                  {materials.map((material) => (
                    <MaterialCard
                      key={material.material_id}
                      material={material}
                      onAddToWatchlist={handleWatchlistToggle}
                      isInWatchlist={watchlist.includes(material.material_id)}
                    />
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-16">
                <h3 className="text-xl font-semibold mb-2">{t('common.noResults', 'No Results')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('search.tryAdjusting', 'Try adjusting your filters or search terms')}
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  {t('filter.clearAllFilters', 'Clear all filters')}
                </Button>
              </div>
            )}

            {/* Pagination */}
            {materials.length >= 20 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(prev => prev - 1)}
                >
                  {t('pagination.previous', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage(prev => prev + 1)}
                >
                  {t('pagination.next', 'Next')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <PostWantedAdDialog open={wantedAdOpen} onClose={() => setWantedAdOpen(false)} />
    </div>
  );
};

export default SearchPage;
